// A coarse XPBD cage drives every visible mesh, including eyes and details.
const MATERIALS = {
  jelly: { edge: 0.00018, volume: 0.00000008, damping: 2.7, recovery: 13, preventInversion: true },
  memorygel: { edge: .008, volume: .00000012, damping: 14, recovery: 9, preventInversion: true,
    contact: { slow: .45, fast: 2, attack: .02, relax: .24, compliance: .0005 } },
  mochi: { edge: 0.0006, volume: 0.0000003, damping: 7, recovery: 9, preventInversion: true },
  dumpling: { edge: .003, volume: .00000015, damping: 12, recovery: 6, preventInversion: true },
  pizza: { edge: .025, volume: .0000004, damping: 14, recovery: 3, preventInversion: true,
    crust: { edge: .001, recovery: 35 } },
  foam: { edge: 0.035, volume: 0.0003, damping: 16, recovery: 8, preventInversion: true },
  wax: { edge: 0.000035, volume: 0.00000005, damping: 6, recovery: 20 },
  slime: { edge: .004, volume: .0000002, damping: 9, recovery: 2.2, reach: 1.65 },
  magnetic: { edge: .012, volume: .0000002, damping: 12, recovery: 10, preventInversion: true },
  clay: { edge: .003, volume: .000005, damping: 10, recovery: 8, plasticity: 2.2 },
  beads: { edge: .002, volume: .000002, damping: 18, recovery: 7, preventInversion: true },
  pouchwater: { edge: .008, volume: .00000008, damping: 8, recovery: 12, reach: .65, preventInversion: true },
  pouch: { edge: .012, volume: .0000003, damping: 15, recovery: 3.5, reach: .65, preventInversion: true },
  mesh: { edge: .00008, volume: .00000008, damping: 5, recovery: 18 },
  // creep: seconds for a held press to sink to the hand (ultra-slow butter).
  butter: { edge: .03, volume: .0003, damping: 15, recovery: 20, preventInversion: true, creep: .6, viscosity: .003 },
  // rateStiffen: a fast hand meets a firm cube; a still or slow hand sinks in.
  sugar: { edge: .0006, volume: .0000003, damping: 6, recovery: 12, preventInversion: true, rateStiffen: 2.5 },
  // Firm silicone pad under push-pop domes: a shallow give that springs back.
  popsilicone: { edge: .004, volume: .0000002, damping: 12, recovery: 14, preventInversion: true },
  // Water-filled tube: nearly incompressible, so a pinch bulges both sides.
  watertube: { edge: .002, volume: .00000006, damping: 7, recovery: 11, preventInversion: true },
  // Wobbly gummy that keeps its volume, so a splat spreads it sideways.
  splatjelly: { edge: .0003, volume: .00000005, damping: 4.5, recovery: 10, preventInversion: true },
};
export class SoftBody {
  // floor: a height the free (unanchored) body cannot sink below. Pressed or
  // pulled down, it squashes against it instead of going through what it sits on.
  // floorFriction: while held, how much of its sideways slide a point touching
  // the floor loses each substep, so a press dents the toy instead of shoving it
  // across the floor. Let go, it slides freely back into shape.
  constructor(min, max, material = 'jelly', {anchorBottom = true, floor = null, floorFriction = 0, resolution = [6,7,6]} = {}) {
    this.min=[...min]; this.max=[...max]; this.settings=MATERIALS[material] ?? MATERIALS.jelly;
    this.anchorBottom=anchorBottom;this.floor=anchorBottom?null:floor;this.floorFriction=floorFriction;
    this.n=[...resolution]; this.count=this.n[0]*this.n[1]*this.n[2];
    this.rest=new Float64Array(this.count*3); this.p=new Float64Array(this.count*3);
    this.prev=new Float64Array(this.count*3); this.velocity=new Float64Array(this.count*3);
    this.candidate=new Float64Array(this.count*3);
    this.mass=new Float64Array(this.count); this.edges=[]; this.tets=[]; this.hold=null; this.activity=0;
    const index=(x,y,z)=>x+this.n[0]*(y+this.n[1]*z);
    for(let z=0;z<this.n[2];z++) for(let y=0;y<this.n[1];y++) for(let x=0;x<this.n[0];x++) {
      const i=index(x,y,z); const q=[x,y,z];
      for(let a=0;a<3;a++) this.rest[i*3+a]=min[a]+(max[a]-min[a])*q[a]/(this.n[a]-1);
      this.mass[i]=this.anchorBottom&&y===0?0:1;
    }
    const edgeSet=new Set();
    for(let z=0;z<this.n[2]-1;z++) for(let y=0;y<this.n[1]-1;y++) for(let x=0;x<this.n[0]-1;x++) {
      const c=[index(x,y,z),index(x+1,y,z),index(x,y+1,z),index(x+1,y+1,z),
        index(x,y,z+1),index(x+1,y,z+1),index(x,y+1,z+1),index(x+1,y+1,z+1)];
      for(const ids of [[0,1,3,7],[0,3,2,7],[0,2,6,7],[0,6,4,7],[0,4,5,7],[0,5,1,7]]) {
        const v=ids.map(i=>c[i]);
        this.tets.push({v,rest:this.volume(this.rest,v),lambda:0});
        for(let a=0;a<4;a++) for(let b=a+1;b<4;b++) {
          const i=Math.min(v[a],v[b]),j=Math.max(v[a],v[b]), key=i*this.count+j;
          if(edgeSet.has(key)) continue; edgeSet.add(key);
          this.edges.push({i,j,rest:Math.hypot(...[0,1,2].map(k=>this.rest[i*3+k]-this.rest[j*3+k])),lambda:0});
        }
      }
    }
    // Blend the baked rim into the yielding cheese end in rest space. A shared
    // cage keeps toppings attached while each region recovers at its own rate.
    this.recovery=null;
    if(this.settings.crust){
      this.recovery=new Float64Array(this.count).fill(this.settings.recovery);
      const rim=new Float64Array(this.count);
      for(let i=0;i<this.count;i++){
        const height=(this.rest[i*3+1]-min[1])/(max[1]-min[1]);
        const t=Math.max(0,Math.min(1,(height-.55)/.25));rim[i]=t*t*(3-2*t);
        this.recovery[i]+=rim[i]*(this.settings.crust.recovery-this.settings.recovery);
      }
      for(const edge of this.edges)edge.compliance=this.settings.edge+(this.settings.crust.edge-this.settings.edge)*(rim[edge.i]+rim[edge.j])*.5;
    }
    this.initialRest=this.rest.slice();this.reset();
  }
  volume(p,v) {
    const a=v[0]*3,b=v[1]*3,c=v[2]*3,d=v[3]*3;
    const bx=p[b]-p[a],by=p[b+1]-p[a+1],bz=p[b+2]-p[a+2];
    const cx=p[c]-p[a],cy=p[c+1]-p[a+1],cz=p[c+2]-p[a+2];
    const dx=p[d]-p[a],dy=p[d+1]-p[a+1],dz=p[d+2]-p[a+2];
    return (bx*(cy*dz-cz*dy)+by*(cz*dx-cx*dz)+bz*(cx*dy-cy*dx))/6;
  }
  bind(point) {
    const supports=[];
    for(let a=0;a<3;a++){
      const t=(point[a]-this.min[a])/(this.max[a]-this.min[a])*(this.n[a]-1);
      const mid=Math.floor(t+.5),axis=[];
      for(let j=mid-1;j<=mid+1;j++){
        const distance=Math.abs(t-j);
        const weight=distance<.5?.75-distance*distance:.5*(1.5-distance)**2;
        axis.push([Math.max(0,Math.min(this.n[a]-1,j)),weight]);
      }
      supports.push(axis);
    }
    const indices=[],weights=[];
    for(const [z,wz]of supports[2])for(const [y,wy]of supports[1])for(const [x,wx]of supports[0]){
      indices.push(x+this.n[0]*(y+this.n[1]*z));weights.push(wx*wy*wz);
    }
    return {indices,weights,point:[...point]};
  }
  sample(binding,out=[0,0,0]) {
    out[0]=binding.point[0];out[1]=binding.point[1];out[2]=binding.point[2];
    for(let k=0;k<binding.indices.length;k++){
      const i=binding.indices[k]*3,w=binding.weights[k];
      out[0]+=(this.p[i]-this.initialRest[i])*w;
      out[1]+=(this.p[i+1]-this.initialRest[i+1])*w;
      out[2]+=(this.p[i+2]-this.initialRest[i+2])*w;
    }
    if(this.anchorBottom)out[1]=Math.max(this.min[1],out[1]);
    else if(this.floor!=null)out[1]=Math.max(this.floor,out[1]);
    return out;
  }
  // The cage's displacement at a rest point, with the same quadratic B-spline
  // weights as bind(), but without allocating: for many points every frame.
  displacementAt(point,out=[0,0,0]) {
    const n=this.n,index=this.scratchIndex??=new Int32Array(9),weight=this.scratchWeight??=new Float64Array(9);
    for(let a=0;a<3;a++){
      const t=(point[a]-this.min[a])/(this.max[a]-this.min[a])*(n[a]-1),mid=Math.floor(t+.5);
      for(let j=0;j<3;j++){
        const g=mid-1+j,distance=Math.abs(t-g);
        weight[a*3+j]=distance<.5?.75-distance*distance:.5*(1.5-distance)**2;index[a*3+j]=Math.max(0,Math.min(n[a]-1,g));
      }
    }
    out[0]=out[1]=out[2]=0;
    for(let z=0;z<3;z++)for(let y=0;y<3;y++)for(let x=0;x<3;x++){
      const w=weight[x]*weight[3+y]*weight[6+z],i=(index[x]+n[0]*(index[3+y]+n[1]*index[6+z]))*3;
      out[0]+=(this.p[i]-this.initialRest[i])*w;out[1]+=(this.p[i+1]-this.initialRest[i+1])*w;out[2]+=(this.p[i+2]-this.initialRest[i+2])*w;
    }
    return out;
  }
  createHold(point) {
    const binding=this.bind(point),weights=new Float64Array(this.count);
    for(let k=0;k<binding.indices.length;k++)weights[binding.indices[k]]+=binding.weights[k];
    const indices=[];let inverseMass=0;
    for(let i=0;i<this.count;i++)if(weights[i]>0){indices.push(i);inverseMass+=this.mass[i]*weights[i]*weights[i];}
    // Preserve the current shape when a deformed surface is grabbed again.
    const anchor=[...point];
    for(const i of indices)for(let a=0;a<3;a++)anchor[a]-=weights[i]*(this.p[i*3+a]-this.initialRest[i*3+a]);
    const hold={start:[...point],target:[...point],requested:[...point],anchor,indices,weights,inverseMass,lambda:new Float64Array(3)};
    if(this.settings.contact)Object.assign(hold,{previousTarget:[...point],resistance:0,motionResistance:0});
    return hold;
  }
  grab(point) {
    this.hold=this.createHold(point);this.supportHold=null;
    this.activity=1;
  }
  support(point) {
    if(!this.hold||!point.every(Number.isFinite))return;
    this.supportHold=this.createHold(point);this.activity=1;
  }
  moveHold(hold,point) {
    if(!hold || !point.every(Number.isFinite)) return;
    const delta=point.map((n,a)=>n-hold.start[a]),length=Math.hypot(...delta);
    const reach=this.settings.reach??Math.min(1,Math.max(...this.max.map((n,a)=>n-this.min[a]))*0.48);
    hold.requested=delta.map((n,a)=>hold.start[a]+n*Math.min(1,reach/(length||1)));
    if(!this.settings.creep&&!this.settings.rateStiffen)hold.target=hold.requested;
  }
  // Materials with a felt response decide how far the held point follows the hand.
  advanceHold(hold,dt) {
    const s=this.settings;if(!hold||(!s.creep&&!s.rateStiffen))return;
    let goal=hold.requested;
    if(s.rateStiffen){
      const last=hold.lastRequested??hold.requested;
      const speed=Math.hypot(...goal.map((n,a)=>n-last[a]))/dt;
      hold.speed=Math.max(speed,(hold.speed??0)*Math.exp(-dt/.3));hold.lastRequested=[...hold.requested];
      const give=1/(1+s.rateStiffen*hold.speed);
      goal=hold.start.map((n,a)=>n+(hold.requested[a]-n)*give);
    }
    if(s.creep){const k=1-Math.exp(-dt/s.creep);hold.target=hold.target.map((n,a)=>n+(goal[a]-n)*k);}
    else hold.target=goal;
  }
  move(point) {this.moveHold(this.hold,point);}
  moveSupport(point) {this.moveHold(this.supportHold,point);}
  release() {this.hold=null;this.supportHold=null;}
  prepareContact(hold,dt) {
    if(!hold||!this.settings.contact)return;
    const {slow,fast}=this.settings.contact;
    const extent=Math.max(...this.max.map((v,a)=>v-this.min[a]));
    const speed=Math.hypot(...hold.target.map((v,a)=>v-hold.previousTarget[a]))/(extent*dt);
    const t=Math.max(0,Math.min(1,(speed-slow)/(fast-slow)));
    hold.motionResistance=t*t*(3-2*t);
    for(let a=0;a<3;a++)hold.previousTarget[a]=hold.target[a];
  }
  relaxContact(hold,dt) {
    if(!hold||!this.settings.contact)return;
    const contact=this.settings.contact;
    const tau=hold.motionResistance>hold.resistance?contact.attack:contact.relax;
    hold.resistance+=(hold.motionResistance-hold.resistance)*(1-Math.exp(-dt/tau));
  }
  solveHold(hold,h) {
    const {anchor,target,indices,weights,inverseMass,lambda}=hold,p=this.p;
    // A compliant contact lets volume constraints react to either hand.
    const compliance=.000002+(this.settings.contact?.compliance??0)*(hold.resistance??0);
    const alpha=compliance/(h*h),denominator=alpha+inverseMass;
    for(let axis=0;axis<3;axis++){
      let error=anchor[axis]-target[axis];
      for(const i of indices)error+=weights[i]*(p[i*3+axis]-this.initialRest[i*3+axis]);
      const dl=(-error-alpha*lambda[axis])/denominator;lambda[axis]+=dl;
      for(const i of indices)p[i*3+axis]+=this.mass[i]*weights[i]*dl;
    }
  }
  updateRestConstraints(){
    for(const e of this.edges)e.rest=Math.hypot(this.rest[e.i*3]-this.rest[e.j*3],this.rest[e.i*3+1]-this.rest[e.j*3+1],this.rest[e.i*3+2]-this.rest[e.j*3+2]);
    for(const t of this.tets)t.rest=this.volume(this.rest,t.v);
  }
  reset() {this.rest.set(this.initialRest);this.updateRestConstraints();this.p.set(this.rest);this.prev.set(this.rest);this.velocity.fill(0);this.release();this.magneticField=null;this.substepHook=null;this.activity=0;this.backtracks=0;}
  step(elapsed) {
    if(!Number.isFinite(elapsed)||elapsed<=0)return;
    const steps=Math.min(8,Math.max(1,Math.ceil(Math.min(elapsed,1/30)/(1/240))));
    const h=Math.min(elapsed,1/30)/steps,s=this.settings,p=this.p,r=this.rest,w=this.mass;
    // Read the input once per physics step, not once per pointer event or XPBD
    // iteration. Resistance then relaxes continuously at the substep cadence.
    if(s.contact){this.prepareContact(this.hold,h*steps);this.prepareContact(this.supportHold,h*steps);}
    this.advanceHold(this.hold,Math.min(elapsed,1/30));this.advanceHold(this.supportHold,Math.min(elapsed,1/30));
    let activity=0;
    for(let sub=0;sub<steps;sub++) {
      if(s.contact){this.relaxContact(this.hold,h);this.relaxContact(this.supportHold,h);}
      this.prev.set(p);
      const damping=Math.exp(-s.damping*h);
      for(let i=0;i<this.count;i++) for(let a=0;a<3;a++) {
        const k=i*3+a;
        this.velocity[k]=(this.velocity[k]+(r[k]-p[k])*(this.recovery?.[i]??s.recovery)*h)*damping;
        p[k]+=this.velocity[k]*h*w[i];
      }
      for(const e of this.edges)e.lambda=0;
      for(const t of this.tets)t.lambda=0;
      if(this.hold)this.hold.lambda.fill(0);
      if(this.supportHold)this.supportHold.lambda.fill(0);
      if(this.magneticField&&!this.hold)this.magneticField.lambda.fill(0);
      for(let iteration=0;iteration<2;iteration++) {
        const defaultAlpha=s.edge/(h*h);
        for(const e of this.edges) {
          const alpha=e.compliance===undefined?defaultAlpha:e.compliance/(h*h);
          const a=e.i*3,b=e.j*3,dx=p[a]-p[b],dy=p[a+1]-p[b+1],dz=p[a+2]-p[b+2];
          const length=Math.hypot(dx,dy,dz); if(length<1e-8)continue;
          const dl=(-(length-e.rest)-alpha*e.lambda)/(w[e.i]+w[e.j]+alpha); e.lambda+=dl;
          for(let axis=0;axis<3;axis++) {const gradient=(p[a+axis]-p[b+axis])/length;p[a+axis]+=w[e.i]*gradient*dl;p[b+axis]-=w[e.j]*gradient*dl;}
        }
        if(this.hold)this.solveHold(this.hold,h);
        if(this.supportHold)this.solveHold(this.supportHold,h);
        if(this.magneticField&&!this.hold){
          // Rest-space targets keep the distant material in place while the
          // selected patch rises. This never captures or replaces a hand hold.
          const field=this.magneticField,alpha=field.compliance/(h*h);
          for(let i=0;i<this.count;i++)for(let a=0;a<3;a++){
            const k=i*3+a,dl=(field.targets[k]-p[k]-alpha*field.lambda[k])/(w[i]+alpha);
            field.lambda[k]+=dl;p[k]+=w[i]*dl;
          }
        }
        const va=s.volume/(h*h);
        // Extra passes touch only compressed cells, preserving free motion elsewhere.
        for(let pass=0;pass<(s.preventInversion?5:1);pass++){
          let corrected=false;
          for(const t of this.tets) {
          if(pass>0&&this.volume(p,t.v)>=t.rest*.15)continue;
          corrected=true;
          const ai=t.v[0],bi=t.v[1],ci=t.v[2],di=t.v[3];
          const a=ai*3,b=bi*3,c=ci*3,d=di*3;
          const bx=p[b]-p[a],by=p[b+1]-p[a+1],bz=p[b+2]-p[a+2];
          const cx=p[c]-p[a],cy=p[c+1]-p[a+1],cz=p[c+2]-p[a+2];
          const dx=p[d]-p[a],dy=p[d+1]-p[a+1],dz=p[d+2]-p[a+2];
          // Keep gradients scalar in the inner solver loop to avoid allocations.
          const gbx=(cy*dz-cz*dy)/6,gby=(cz*dx-cx*dz)/6,gbz=(cx*dy-cy*dx)/6;
          const gcx=(dy*bz-dz*by)/6,gcy=(dz*bx-dx*bz)/6,gcz=(dx*by-dy*bx)/6;
          const gdx=(by*cz-bz*cy)/6,gdy=(bz*cx-bx*cz)/6,gdz=(bx*cy-by*cx)/6;
          const gax=-gbx-gcx-gdx,gay=-gby-gcy-gdy,gaz=-gbz-gcz-gdz;
          const volume=bx*gbx+by*gby+bz*gbz;
          const barrier=s.preventInversion&&volume<t.rest*.15;
          const compliance=barrier?0:va;
          let denominator=compliance;
          denominator+=w[ai]*(gax**2+gay**2+gaz**2);
          denominator+=w[bi]*(gbx**2+gby**2+gbz**2);
          denominator+=w[ci]*(gcx**2+gcy**2+gcz**2);
          denominator+=w[di]*(gdx**2+gdy**2+gdz**2);
          if(denominator<1e-12)continue;
          const dl=(-(volume-(barrier?t.rest*.15:t.rest))-compliance*t.lambda)/denominator;
          if(!barrier)t.lambda+=dl;
          p[a]+=w[ai]*gax*dl;p[a+1]+=w[ai]*gay*dl;p[a+2]+=w[ai]*gaz*dl;
          p[b]+=w[bi]*gbx*dl;p[b+1]+=w[bi]*gby*dl;p[b+2]+=w[bi]*gbz*dl;
          p[c]+=w[ci]*gcx*dl;p[c+1]+=w[ci]*gcy*dl;p[c+2]+=w[ci]*gcz*dl;
          p[d]+=w[di]*gdx*dl;p[d+1]+=w[di]*gdy*dl;p[d+2]+=w[di]*gdz*dl;
          }
          if(!corrected)break;
        }
      }
      if(this.anchorBottom)for(let i=0;i<this.count;i++)p[i*3+1]=Math.max(this.min[1],p[i*3+1]);
      else if(this.floor!=null){
        const touch=this.floor+(this.max[1]-this.min[1])*.02,keep=this.hold?1-this.floorFriction:1;
        // Weight: a body that has sprung up off its floor settles back onto it
        // within a moment, keeping its shape, instead of hanging in the air.
        // feet: rest points on the underside of its skin (a ball touches the
        // floor at one point, not at the cage's corners).
        let lift=Infinity;
        if(this.feet?.length){const d=this.footScratch??=[0,0,0];for(const f of this.feet)lift=Math.min(lift,this.displacementAt(f,d)[1]);}
        else{for(let i=1;i<p.length;i+=3)lift=Math.min(lift,p[i]-this.floor);}
        if(lift>0){const drop=lift*Math.min(1,h*20);for(let i=1;i<p.length;i+=3)p[i]-=drop;}
        for(let i=0;i<this.count;i++){
          const k=i*3;p[k+1]=Math.max(this.floor,p[k+1]);
          if(keep<1&&p[k+1]<=touch){p[k]=this.prev[k]+(p[k]-this.prev[k])*keep;p[k+2]=this.prev[k+2]+(p[k+2]-this.prev[k+2])*keep;}
        }
      }
      // Keep a final safeguard for abrupt inputs and floor projection. Ordinary
      // compression is resolved locally above before this fallback is needed.
      const valid=()=>this.tets.every(t=>this.volume(p,t.v)>=t.rest*.08);
      if(s.preventInversion&&!valid()){
        this.backtracks++;
        this.candidate.set(p);let lo=0,hi=1;
        for(let iteration=0;iteration<8;iteration++){
          const fraction=(lo+hi)*.5;
          for(let i=0;i<p.length;i++)p[i]=this.prev[i]+(this.candidate[i]-this.prev[i])*fraction;
          if(valid())lo=fraction;else hi=fraction;
        }
        for(let i=0;i<p.length;i++)p[i]=this.prev[i]+(this.candidate[i]-this.prev[i])*lo;
      }
      if(s.plasticity&&this.hold){
        for(let i=0;i<p.length;i++){
          const change=(p[i]-r[i])*s.plasticity*h;
          r[i]=Math.max(this.initialRest[i]-.5,Math.min(this.initialRest[i]+.5,r[i]+change));
        }
        if(!this.anchorBottom){
          // Keep plastic shape changes, but let the existing recovery spring
          // return the body to its initial center after a free grab.
          let dx=0,dy=0,dz=0;
          for(let i=0;i<r.length;i+=3){dx+=r[i]-this.initialRest[i];dy+=r[i+1]-this.initialRest[i+1];dz+=r[i+2]-this.initialRest[i+2];}
          dx/=this.count;dy/=this.count;dz/=this.count;
          // On a floor, a flattened shape keeps its bottom on the floor rather
          // than its middle where it was, or it would hang in the air.
          if(this.floor!=null){let low=Infinity;for(let i=1;i<r.length;i+=3)low=Math.min(low,r[i]);dy=low-this.min[1];}
          for(let i=0;i<r.length;i+=3){r[i]-=dx;r[i+1]-=dy;r[i+2]-=dz;}
        }
        this.updateRestConstraints();
      }
      if(s.viscosity){
        // Thick material: each substep keeps only part of the solved motion, so
        // a released dent rises slowly instead of snapping back to shape.
        const keep=1-Math.exp(-h/s.viscosity);
        for(let i=0;i<p.length;i++)p[i]=this.prev[i]+(p[i]-this.prev[i])*keep;
      }
      // Optional contact or carry projection (e.g. a toy pressed onto glass).
      if(this.substepHook)this.substepHook(p,this.prev,r,h);
      for(let i=0;i<p.length;i++) {
        this.velocity[i]=(p[i]-this.prev[i])/h;
        activity=Math.max(activity,Math.abs(this.velocity[i]),Math.abs(p[i]-r[i]));
      }
    }
    if(!this.hold&&!this.magneticField&&!this.substepHook&&activity<.002){this.p.set(this.rest);this.velocity.fill(0);activity=0;}
    this.activity=activity;
  }
}
